import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const QualityAssurance = () => {
  const [activeCategory, setActiveCategory] = useState('processes');

  const categories = [
    { id: 'processes', label: 'Quality Processes', icon: 'ClipboardCheck' },
    { id: 'certifications', label: 'Certifications', icon: 'Award' },
    { id: 'facilities', label: 'Facilities', icon: 'Building' },
    { id: 'equipment', label: 'Equipment', icon: 'Cog' }
  ];

  const qualityItems = {
    processes: [
      {
        id: 1,
        title: 'Material Inspection',
        description: 'Rigorous testing of raw materials before production begins to ensure only the highest quality inputs.',
        icon: 'Search',
        iconColor: 'text-blue-500',
        stats: ['99.8% Pass Rate', '5-Point Verification', 'ISO 9001 Standard']
      },
      {
        id: 2,
        title: 'Production Monitoring',
        description: 'Continuous supervision throughout the manufacturing process with multiple quality checkpoints.',
        icon: 'Monitor',
        iconColor: 'text-green-500',
        stats: ['24/7 Monitoring', 'Digital Tracking', 'Real-time Adjustments']
      },
      {
        id: 3,
        title: 'Final Product Testing',
        description: 'Comprehensive evaluation of finished products, including stress tests and durability assessments.',
        icon: 'CheckCircle',
        iconColor: 'text-purple-500',
        stats: ['100% Testing Rate', 'Multiple Test Scenarios', '3-Stage Approval']
      },
      {
        id: 4,
        title: 'Pre-Shipment Inspection',
        description: 'Final quality control check before products are cleared for export to ensure they meet all specifications.',
        icon: 'Truck',
        iconColor: 'text-orange-500',
        stats: ['Expert Inspectors', 'Detailed Documentation', 'Client Approval']
      }
    ],
    certifications: [
      {
        id: 1,
        title: 'ISO 9001:2015',
        description: 'International standard for quality management systems, ensuring consistent quality and customer satisfaction.',
        icon: 'Award',
        iconColor: 'text-yellow-500',
        stats: ['Globally Recognized', 'Annual Audits', 'Full Compliance']
      },
      {
        id: 2,
        title: 'Fair Trade Certified',
        description: 'Verification that products meet rigorous social, environmental, and economic standards.',
        icon: 'Handshake',
        iconColor: 'text-green-500',
        stats: ['Ethical Sourcing', 'Community Support', 'Sustainable Practices']
      },
      {
        id: 3,
        title: 'Organic Certification',
        description: 'Confirmation that agricultural products have been produced according to organic standards without synthetic inputs.',
        icon: 'Leaf',
        iconColor: 'text-emerald-500',
        stats: ['No Pesticides', 'Non-GMO', 'Environmentally Friendly']
      },
      {
        id: 4,
        title: 'HACCP Certification',
        description: 'Food safety management system ensuring that food products are safe for consumption.',
        icon: 'Shield',
        iconColor: 'text-red-500',
        stats: ['Hazard Analysis', 'Critical Control Points', 'Safe Production']
      }
    ],
    facilities: [
      {
        id: 1,
        title: 'Quality Control Laboratory',
        description: 'State-of-the-art testing facility for comprehensive analysis of raw materials and finished products.',
        icon: 'Flask',
        iconColor: 'text-blue-500',
        stats: ['Modern Equipment', 'Specialized Testing', 'Expert Technicians']
      },
      {
        id: 2,
        title: 'Clean Room Production',
        description: 'Controlled environment manufacturing spaces with regulated temperature, humidity, and air quality.',
        icon: 'Wind',
        iconColor: 'text-cyan-500',
        stats: ['Controlled Environment', 'Contamination Prevention', 'Precision Manufacturing']
      },
      {
        id: 3,
        title: 'Storage Facilities',
        description: 'Climate-controlled warehousing to maintain product integrity during storage and before shipping.',
        icon: 'Warehouse',
        iconColor: 'text-indigo-500',
        stats: ['Temperature Control', 'Inventory Management', 'Secure Storage']
      },
      {
        id: 4,
        title: 'Training Center',
        description: 'Dedicated facility for ongoing staff education and quality assurance training programs.',
        icon: 'GraduationCap',
        iconColor: 'text-purple-500',
        stats: ['Regular Training', 'Skill Development', 'Knowledge Sharing']
      }
    ],
    equipment: [
      {
        id: 1,
        title: 'Spectrophotometers',
        description: 'Precision instruments for measuring color consistency and material composition analysis.',
        icon: 'ScanLine',
        iconColor: 'text-pink-500',
        stats: ['High Accuracy', 'Color Verification', 'Material Analysis']
      },
      {
        id: 2,
        title: 'Tensile Testing Machines',
        description: 'Equipment for measuring the strength and durability of materials under tension, compression, or flexure.',
        icon: 'Zap',
        iconColor: 'text-yellow-500',
        stats: ['Strength Testing', 'Durability Assessment', 'Failure Analysis']
      },
      {
        id: 3,
        title: 'Digital Microscopes',
        description: 'Advanced imaging tools for detailed inspection of product surfaces and internal structures.',
        icon: 'Eye',
        iconColor: 'text-teal-500',
        stats: ['Microscopic Inspection', 'Digital Imaging', 'Detail Analysis']
      },
      {
        id: 4,
        title: 'Automated Packaging Systems',
        description: 'High-precision machinery ensuring consistent packaging quality and protection during shipping.',
        icon: 'Package',
        iconColor: 'text-orange-500',
        stats: ['Consistency Control', 'Damage Prevention', 'Efficiency']
      }
    ]
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <section id="quality-assurance" className="py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Our Quality Assurance
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Rigorous standards and processes ensure every export meets international quality benchmarks.
          </p>
        </motion.div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-surface-100 text-text-secondary hover:bg-surface-200'
              }`}
              onClick={() => setActiveCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Icon name={category.icon} size={18} />
              <span>{category.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Quality Items Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {qualityItems[activeCategory]?.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1, ease: "easeOut" }}
              className="glass-card overflow-hidden group hover:shadow-elevation-lg transition-all duration-300"
            >
              <div className="relative h-48 overflow-hidden flex justify-center items-center">
                <Icon name={item.icon} size={48} className={item.iconColor} />
              </div>
              
              <div className="p-5">
                <h3 className="text-xl font-bold text-text-primary mb-4">{item.title}</h3>
                <p className="text-text-secondary mb-4">
                  {item.description}
                </p>
                
                <div className="space-y-2">
                  {item.stats.map((stat, statIndex) => (
                    <div key={statIndex} className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-accent"></div>
                      <span className="text-sm font-medium text-text-primary">{stat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quality Statement */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeInUp}
          className="mt-16 glass-card p-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-l-4 border-primary"
        >
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="w-16 h-16 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={32} className="text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-primary mb-3">Our Quality Commitment</h3>
              <p className="text-text-secondary mb-4">
                Every product that leaves our facilities undergoes a comprehensive quality assurance process. 
                We maintain stringent standards throughout the entire export journey, from sourcing to final delivery, 
                ensuring that our clients receive products that meet or exceed international quality benchmarks.
              </p>
              <div className="flex flex-wrap gap-4 mt-4">
                <div className="flex items-center space-x-2 bg-surface-100 px-4 py-2 rounded-full">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium">100% Inspection</span>
                </div>
                <div className="flex items-center space-x-2 bg-surface-100 px-4 py-2 rounded-full">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium">Quality Focused</span>
                </div>
                <div className="flex items-center space-x-2 bg-surface-100 px-4 py-2 rounded-full">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium">Quality Guarantee</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeInUp}
          className="mt-12 text-center"
        >
          <h3 className="text-2xl font-bold text-primary mb-4">
            Experience Our Quality Standards
          </h3>
          <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
            Request product samples to experience our quality firsthand before placing your export order.
          </p>
          <a 
            href="https://wa.me/2347061704302"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-accent px-8 py-4 font-semibold flex items-center space-x-2 mx-auto"
          >
            <Icon name="MessageCircle" size={20} />
            <span>Request Sample Kit</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default QualityAssurance;